'use client';
import { AddShoppingCart } from '@mui/icons-material';
import NavigateNextBefore from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Button, IconButton, LinearProgress, Typography } from '@mui/material';
import { sendGTMEvent } from '@next/third-parties/google';
import ButtonRequestTestSubmission from '@/components/test/button-request-test-submission';
import ButtonStartNewTestRun from '@/components/test/button-start-new-test-run';
import UnitPanel from '@/components/unit/unit-panel';
import { useAuth, useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import { isExercise } from '@/types';
import { getAddProductToCartUrl, useRoute } from '@/utils/client/route';

export default function CourseBottomBar() {
  const { goToRoute } = useRoute();
  const {
    courseStatus,
    hasChapterAccess,
    restartUserExercise,
    selectedChapter,
    selectedCourse,
    updateUserExercise,
    isLoading,
    canRenewTest,
  } = useCourses();
  const { isLoggedIn } = useAuth();
  const { toggleDialog } = useViewStore();

  if (!selectedCourse || !selectedChapter) {
    return (
      <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'space-between' }}>
        <IconButton
          edge="start"
          aria-label="open drawer"
          color="default"
          disabled={true}
          size="small"
          style={{ borderRadius: 6, paddingRight: 8 }}>
          <NavigateNextBefore fontSize="large" />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Voriges Kapitel
          </Box>
        </IconButton>

        <IconButton
          edge="end"
          aria-label="open drawer"
          color="default"
          disabled={true}
          size="small"
          style={{ borderRadius: 6, paddingLeft: 8 }}>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Nächstes Kapitel
          </Box>
          <NavigateNextIcon fontSize="large" />
        </IconButton>
      </Box>
    );
  }

  const { curriculum } = selectedCourse;
  const isTest = selectedCourse.type === 'Test';
  const { id, slug, type } = selectedChapter;
  const selectedChapterIndex = curriculum.findIndex((chapterItem) => chapterItem.item._id === id);
  const totalChapters = curriculum?.length;

  const handleExercise = (type: string) => {
    if (isExercise(selectedChapter)) {
      let event: Record<string, string | number> | undefined = {
        event: `${type}_user_exercise`,
        category: 'Exercise',
        action: type,
        label: slug,
      };
      switch (type) {
        case 'restart':
          restartUserExercise();
          break;
        case 'save':
          updateUserExercise(false);
          break;
        case 'submit':
          if (isTest) {
            const content = (
              <Typography>
                Du kannst den Vorgang nicht rückgängig machen und die Fragen aus dieser Übung danach
                nicht mehr bearbeiten. Du kannst die anderen Übungen weiter bearbeiten, solange Du
                diese noch nicht hast auswerten lassen.
              </Typography>
            );
            toggleDialog({
              action: 'submitTestExercise',
              actionLabel: 'Übung auswerten',
              content,
              title: 'Möchtest Du die Übung final auswerten?',
            });
            // Skip event as dialog needs confirmation first
            event = undefined;
          } else {
            updateUserExercise(true);
          }
          break;
        default:
          break;
      }
      if (event) sendGTMEvent(event);
    }
  };

  const onChapterNavigation = (direction: 'prev' | 'next') => {
    let nextItem = null;
    let i = selectedChapterIndex;
    if (direction === 'prev' && selectedChapterIndex >= 1) {
      while (!nextItem && i > 0) {
        i -= 1;
        if (curriculum[i].type !== 'ChapterSection') {
          nextItem = curriculum[i];
        }
      }
    } else if (direction === 'next' && selectedChapterIndex < totalChapters - 1) {
      while (!nextItem && i < totalChapters - 1) {
        i += 1;
        if (curriculum[i].type !== 'ChapterSection') {
          nextItem = curriculum[i];
        }
      }
    }
    if (nextItem?.item.slug && selectedCourse?.slug) {
      goToRoute('myCourseLearn', {
        courseSlug: selectedCourse.slug,
        chapterSlug: nextItem.item.slug,
      });
    }
  };

  const getChapterNavDisabled = (direction: 'prev' | 'next'): boolean => {
    let result = true;
    if (direction === 'prev') {
      let firstItem = null;
      let i = 0;
      // Find first item that is not a chapter section
      while (!firstItem && i < curriculum.length) {
        if (curriculum[i].type !== 'ChapterSection') {
          firstItem = curriculum[i];
        } else {
          i += 1;
        }
      }
      result = selectedChapterIndex === -1 || selectedChapterIndex === i;
    }
    if (direction === 'next') {
      result = selectedChapterIndex === -1 || selectedChapterIndex === totalChapters - 1;
    }
    return result;
  };

  const showTestSubmissionButton =
    isLoggedIn &&
    isTest &&
    courseStatus?.hasMembership &&
    courseStatus?.chapters.progress === 100 &&
    courseStatus?.status === 'active';
  const showUnitPanel = !isLoading && !isTest && isLoggedIn && type === 'Unit' && hasChapterAccess;

  return (
    <>
      <IconButton
        edge="start"
        aria-label="open drawer"
        color="default"
        onClick={() => onChapterNavigation('prev')}
        disabled={getChapterNavDisabled('prev')}
        size="small"
        style={{ borderRadius: 6, paddingRight: 8 }}>
        <NavigateNextBefore fontSize="large" />
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          Voriges Kapitel
        </Box>
      </IconButton>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          justifyContent: 'center',
          '& > .MuiButton-root': {
            alignSelf: 'center',
          },
        }}>
        {isLoading && <LinearProgress />}
        {showUnitPanel && <UnitPanel />}
        {!isLoading && isExercise(selectedChapter) && (
          <>
            {selectedChapter.status === 'active' &&
              courseStatus?.status === 'active' &&
              isLoggedIn &&
              (isTest ? (
                <ButtonRequestTestSubmission />
              ) : (
                <Button variant="contained" onClick={() => handleExercise('submit')}>
                  Auswerten
                </Button>
              ))}
            {selectedChapter.status === 'submitted' &&
              isLoggedIn &&
              hasChapterAccess &&
              selectedChapter.repeatable === 'always' && (
                <Button variant="contained" onClick={() => handleExercise('restart')}>
                  Wiederholen
                </Button>
              )}
            {isTest &&
              courseStatus?.status === 'submitted' &&
              courseStatus?.repetitions?.remaining === 0 && (
                <Button
                  color="secondary"
                  variant="contained"
                  target="_blank"
                  startIcon={<AddShoppingCart />}
                  href={getAddProductToCartUrl(selectedCourse.wpProductId)}>
                  Warenkorb
                </Button>
              )}
            {canRenewTest && <ButtonStartNewTestRun />}
            {!getChapterNavDisabled('next') && showTestSubmissionButton && (
              <ButtonRequestTestSubmission />
            )}
          </>
        )}
      </Box>
      {getChapterNavDisabled('next') && showTestSubmissionButton ? (
        <ButtonRequestTestSubmission />
      ) : (
        <IconButton
          edge="end"
          aria-label="open drawer"
          color="default"
          onClick={() => onChapterNavigation('next')}
          disabled={getChapterNavDisabled('next')}
          size="small"
          style={{ borderRadius: 6, paddingLeft: 8 }}>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Nächstes Kapitel
          </Box>
          <NavigateNextIcon fontSize="large" />
        </IconButton>
      )}
    </>
  );
}
